import { useState, useRef, useCallback } from 'react';
import { Upload, X, FileText, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import api from '@/lib/api';

const ACCEPTED_FORMATS = ['.e57', '.las', '.laz', '.ply', '.pcd', '.pts', '.ptx', '.xyz', '.obj', '.fbx', '.rcp', '.rcs'];

export default function FileUploadZone({ projectId, sessionId, onUploadComplete }) {
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const addFiles = useCallback((newFiles) => {
    const fileList = Array.from(newFiles).map(f => ({
      file: f,
      name: f.name,
      size: f.size,
      status: 'pending', // pending, uploading, complete, error
      progress: 0,
      result: null,
      error: null,
    }));
    setFiles(prev => [...prev, ...fileList]);
  }, []);

  const uploadFile = useCallback(async (fileEntry, index) => {
    setFiles(prev => prev.map((f, i) => i === index ? { ...f, status: 'uploading', progress: 0 } : f));
    try {
      const formData = new FormData();
      formData.append('file', fileEntry.file);

      const xhr = new XMLHttpRequest();
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

      await new Promise((resolve, reject) => {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 100);
            setFiles(prev => prev.map((f, i) => i === index ? { ...f, progress: pct } : f));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            const result = JSON.parse(xhr.responseText);
            setFiles(prev => prev.map((f, i) => i === index ? { ...f, status: 'complete', progress: 100, result } : f));
            resolve(result);
          } else {
            reject(new Error(`Upload failed: ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.open('POST', `${backendUrl}/api/v1/projects/${projectId}/scan3d/sessions/${sessionId}/upload`);
        const token = localStorage.getItem('token');
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.send(formData);
      });
    } catch (err) {
      setFiles(prev => prev.map((f, i) => i === index ? { ...f, status: 'error', error: err.message } : f));
    }
  }, [projectId, sessionId]);

  const uploadAll = useCallback(async () => {
    const pending = files.map((f, i) => ({ f, i })).filter(({ f }) => f.status === 'pending');
    for (const { f, i } of pending) {
      await uploadFile(f, i);
    }
    if (onUploadComplete) onUploadComplete();
  }, [files, uploadFile, onUploadComplete]);

  const removeFile = useCallback((index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const fmtSize = (bytes) => {
    if (bytes >= 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
    if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    return `${(bytes / 1024).toFixed(0)} KB`;
  };

  const hasUploading = files.some(f => f.status === 'uploading');
  const hasPending = files.some(f => f.status === 'pending');

  return (
    <div className="space-y-3" data-testid="scan3d-upload-zone">
      {/* Drop Zone */}
      <div
        className={`rounded-lg border-2 border-dashed p-6 text-center cursor-pointer transition-all ${dragOver ? 'border-cyan-400 bg-cyan-400/5' : 'border-[var(--border-color)] hover:border-cyan-400/40'}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPTED_FORMATS.join(',')}
          className="hidden"
          onChange={(e) => { if (e.target.files?.length) addFiles(e.target.files); e.target.value = ''; }}
          data-testid="scan3d-file-input"
        />
        <Upload className="w-8 h-8 mx-auto mb-2" style={{ color: dragOver ? '#06b6d4' : 'var(--text-secondary)' }} />
        <p className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>Drag & drop scan files here or click to browse</p>
        <p className="text-[10px] mt-1" style={{ color: 'var(--text-secondary)' }}>
          Supported: {ACCEPTED_FORMATS.join(', ')}
        </p>
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((f, i) => (
            <div key={`${f.name}-${i}`} className="flex items-center gap-3 p-2.5 rounded-lg" style={{ background: 'var(--input-bg)', border: '1px solid var(--border-color)' }} data-testid={`upload-file-${i}`}>
              <FileText className="w-4 h-4 shrink-0" style={{ color: '#06b6d4' }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium truncate" style={{ color: 'var(--text-primary)' }}>{f.name}</span>
                  <span className="text-[9px]" style={{ color: 'var(--text-secondary)' }}>{fmtSize(f.size)}</span>
                </div>
                {f.status === 'uploading' && (
                  <div className="flex items-center gap-2 mt-1">
                    <Progress value={f.progress} className="h-1.5 flex-1" />
                    <span className="text-[9px] font-mono" style={{ color: '#f59e0b' }}>{f.progress}%</span>
                  </div>
                )}
                {f.status === 'error' && <p className="text-[9px] text-red-400 mt-0.5">{f.error}</p>}
              </div>
              <div className="shrink-0">
                {f.status === 'pending' && <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: 'var(--input-bg)', color: 'var(--text-secondary)' }}>Pending</span>}
                {f.status === 'uploading' && <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-400" />}
                {f.status === 'complete' && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />}
                {f.status === 'error' && <AlertTriangle className="w-3.5 h-3.5 text-red-400" />}
              </div>
              {f.status !== 'uploading' && (
                <button onClick={(e) => { e.stopPropagation(); removeFile(i); }} className="p-1 rounded hover:bg-red-500/10">
                  <X className="w-3 h-3" style={{ color: 'var(--text-secondary)' }} />
                </button>
              )}
            </div>
          ))}

          {/* Upload button */}
          {hasPending && !hasUploading && (
            <button onClick={uploadAll} className="w-full py-2 rounded-lg text-xs font-medium bg-cyan-500 hover:bg-cyan-600 text-white transition-colors" data-testid="scan3d-upload-btn">
              <Upload className="w-3.5 h-3.5 inline mr-1.5" />Upload {files.filter(f => f.status === 'pending').length} file{files.filter(f => f.status === 'pending').length > 1 ? 's' : ''}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
